import { createStyleString } from "@capsizecss/core"
import { css } from "library/styled"
import { soehne } from "./fonts/typography"

export const transparentText = css`
	/* stylelint-disable-next-line property-no-vendor-prefix  */
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	-moz-text-fill-color: transparent;
	background-size: 100%;
	background-clip: text;
`

export const clampText = (lines: number) => css`
	overflow: hidden;
	text-overflow: ellipsis;
	/* stylelint-disable-next-line property-no-vendor-prefix  */
	-webkit-text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: ${lines};
`

const soehneMetrics = {
	capHeight: 718,
	ascent: 1037,
	descent: -289,
	lineGap: 0,
	unitsPerEm: 1000,
}

/**
 * if text height is capped to baseline, use this utility to set text size
 */
const makeStyleString = (options: Parameters<typeof createStyleString>[1]) =>
	createStyleString("&", options).replaceAll(".&", "")

const textStyles = {
	/**
	 * if you need to add one-off styles, do that here!
	 */
	custom: {
		h1: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 112,
				leading: 112 * 0.8,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -3.36px;
		`,
		quotingH1: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 112,
				leading: 112 * 1.2,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -3.36px;
		`,
		h4: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 64,
				leading: 64,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -1.92px;
		`,
		h5: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 48,
				leading: 48,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -1.44px;
		`,
		bodyL: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 24,
				leading: 24,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.72px;
		`,

		bodyR: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 20,
				leading: 20,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.6px;
		`,
		bodyS: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 16,
				leading: 16,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.48px;
		`,
		bodyXS: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 14,
				leading: 14,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.42px;
		`,
		bodyXSAlt: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 14,
				leading: 14 * 1.1,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 500;
			letter-spacing: -0.42px;
		`,
		bodyXXSTablet: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 13.875,
				leading: 13.875 * 1.4,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.416px;
		`,
		bodyXXSMobile: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 10,
				leading: 10 * 1.4,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.3px;
		`,
		h5Mobile: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 32,
				leading: 32 * 1.2,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.96px;
		`,
		bodyXXS: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 12,
				leading: 12,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.36px;
		`,
		bodyXXXS: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 10,
				leading: 10,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.3px;
		`,

		bodyXXXXS: css`
			font-family: ${soehne.style.fontFamily}, sans-serif;
			${makeStyleString({
				fontSize: 8,
				leading: 8,
				fontMetrics: soehneMetrics,
			})};
			font-style: normal;
			font-weight: 400;
			letter-spacing: -0.26px;
		`,
	},

	h1: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 112,
			leading: 112 * 1,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -3.36px;
	`,

	h2: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 96,
			leading: 96 * 1.2,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -2.88px;
	`,

	h3: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 72,
			leading: 72 * 1.2,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -2.16px;
	`,

	h4: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 64,
			leading: 64 * 1.2,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -1.92px;
	`,

	h5: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 48,
			leading: 48 * 1.2,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -1.44px;
	`,

	h6: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 36,
			leading: 36 * 1.2,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -1.08px;
	`,

	titleL: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 20,
			leading: 20 * 1.3,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: 2px;
		text-transform: uppercase;
	`,

	titleR: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 16,
			leading: 16 * 1.3,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: 1.6px;
		text-transform: uppercase;
	`,

	titleS: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 12,
			leading: 12 * 1.3,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: 1.2px;
		text-transform: uppercase;
	`,

	titleXS: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 10,
			leading: 10,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: 1.1px;
		text-transform: uppercase;
	`,
	titleXXS: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 6,
			leading: 6 * 1.2,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: 0.48px;
		text-transform: uppercase;
	`,

	bodyXL: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 28,
			leading: 28 * 1.4,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -0.84px;
	`,

	bodyL: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 24,
			leading: 24 * 1.4,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -0.72px;
	`,

	bodyR: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 20,
			leading: 20 * 1.4,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -0.6px;
	`,

	bodyS: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 16,
			leading: 16 * 1.4,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -0.48px;
	`,

	bodySL: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 18,
			leading: 18 * 1.6,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -0.54px;
	`,

	bodyXS: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 14,
			leading: 14 * 1.4,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -0.42px;
	`,

	bodyXXS: css`
		font-family: ${soehne.style.fontFamily}, sans-serif;
		${makeStyleString({
			fontSize: 12,
			leading: 12 * 1.4,
			fontMetrics: soehneMetrics,
		})};
		font-style: normal;
		font-weight: 400;
		letter-spacing: -0.36px;
	`,
}

export default textStyles
